import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Palette, Upload, Clock } from 'lucide-react';
import CambiarPasswordCard from './CambiarPasswordCard';
import ColaboradoresCard from './ColaboradoresCard';
import AutoCierreCard from './AutoCierreCard';

const COLORES = ['#18181B', '#0F766E', '#1D4ED8', '#B91C1C', '#7C3AED', '#EA580C', '#059669', '#DB2777'];

const AjustesTab = () => {
  const { user, refreshUser } = useAuth();
  const [color, setColor] = useState(user?.color || '#18181B');
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const logo = user?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.logo_url}` : null;

  useEffect(() => { setColor(user?.color || '#18181B'); }, [user]);

  const guardarColor = async () => {
    setGuardando(true);
    try {
      await api.put('/admin/branding', { color });
      await refreshUser();
      toast.success('Color de marca actualizado');
    } catch {
      toast.error('Error al guardar el color');
    } finally { setGuardando(false); }
  };

  const subirLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/admin/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshUser();
      toast.success('Logo actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el logo');
    } finally { setSubiendo(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="ajustes-tab">
      <Card className="border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Upload className="h-5 w-5" /> Logo de la empresa
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-xl border border-zinc-200 bg-white flex items-center justify-center overflow-hidden">
              {logo ? <img src={logo} alt="logo" className="h-full w-full object-contain" data-testid="ajustes-logo-preview" />
                : <Clock className="h-8 w-8 text-zinc-300" />}
            </div>
            <div className="flex-1">
              <p className="text-sm text-zinc-600 mb-2">PNG, JPG o WEBP · máx. 2 MB</p>
              <input id="logo-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={subirLogo}
                className="hidden" data-testid="ajustes-logo-input" />
              <Button asChild variant="outline" disabled={subiendo}>
                <label htmlFor="logo-file" className="cursor-pointer" data-testid="ajustes-logo-btn">
                  {subiendo ? 'Subiendo...' : 'Subir logo'}
                </label>
              </Button>
            </div>
          </div>
          <p className="text-xs text-zinc-400">El logo aparecerá en el kiosko, el panel y los informes PDF.</p>
        </CardContent>
      </Card>

      <Card className="border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Palette className="h-5 w-5" /> Color de marca
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {COLORES.map((c) => (
              <button key={c} onClick={() => setColor(c)} data-testid={`color-${c.replace('#', '')}`}
                className={`h-10 w-10 rounded-lg border-2 transition-all ${color === c ? 'border-zinc-900 scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              className="h-10 w-14 rounded border border-zinc-200 cursor-pointer" data-testid="color-picker" />
            <Input value={color} onChange={(e) => setColor(e.target.value)} className="w-32" data-testid="color-input" />
            <Button onClick={guardarColor} disabled={guardando} className="bg-zinc-900 hover:bg-zinc-800" data-testid="save-color-btn">
              {guardando ? 'Guardando...' : 'Guardar color'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <CambiarPasswordCard />

      {user?.role === 'admin' && <AutoCierreCard />}

      {user?.role === 'admin' && <ColaboradoresCard />}
    </div>
  );
};

export default AjustesTab;
